import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import ModalBasicLayout, {
  useModalRequestClose,
} from "../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import TextField from "../../../components/Form/TextField";
import ProduceImageCell from "../produce/ProduceImageCell";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { startRehostProduceRowImages } from "../../../actions/creativeTemplatesProduce";
import {
  TASK_ALERT_ERROR_MESSAGE_BG,
  TASK_ALERT_ERROR_MESSAGE_BORDER,
} from "../../../utils/colors";
import {
  BUTTON_CANCEL,
  FIELD_REQUIRED,
  TEMPLATES_EDITOR_PREVIEW_VARIABLES_DESCRIPTION,
  TEMPLATES_EDITOR_PREVIEW_VARIABLES_TITLE,
  TEMPLATES_PRODUCE_IMAGES_UNUSABLE,
  TEMPLATES_PRODUCE_PREVIEW,
  TEMPLATES_PRODUCE_VALIDATION_ERROR,
  TEMPLATES_PRODUCE_VALIDATION_HIGHLIGHT,
} from "../../../i18n/keysTranslations";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import {
  createEmptyRow,
  getInvalidProduceCellKeysByRow,
  getProduceImageCellStatus,
  hasInvalidHighlightPhrase,
  isImageHttpUrl,
  rowsHaveFailedProduceImages,
} from "../produce/produceRows";
import { produceFieldLabelSx, produceSelectSx } from "../produce/produceCardStyles";
import { resizableMultilineSx } from "./canvasWorkspace";

const optionalRules = { required: false };

const PreviewVariablesActions = ({ isCheckingImages, onConfirm }) => {
  const requestClose = useModalRequestClose();

  return (
    <ContainerModalActions
      hasPadding={false}
      propsSecondaryButton={{
        showButton: true,
        text: BUTTON_CANCEL,
        onClick: requestClose,
        disabled: isCheckingImages,
      }}
      propsPrimaryButton={{
        showButton: true,
        text: TEMPLATES_PRODUCE_PREVIEW,
        onClick: onConfirm,
        disabled: isCheckingImages,
      }}
    />
  );
};

const TemplateEditorPreviewVariablesModal = ({
  open = false,
  columns = [],
  onClose = () => {},
  onSubmit = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [invalidKeys, setInvalidKeys] = useState(() => new Set());
  const [checkingImageUrls, setCheckingImageUrls] = useState(() => new Set());
  const [failedImageUrls, setFailedImageUrls] = useState(() => new Set());
  const { control, getValues, setValue, reset, watch } = useForm({
    defaultValues: {},
  });
  const row = watch();
  const isCheckingImages = checkingImageUrls.size > 0;

  useEffect(() => {
    if (!open) return;
    reset(createEmptyRow({ columns }));
    setInvalidKeys(new Set());
    setCheckingImageUrls(new Set());
    setFailedImageUrls(new Set());
  }, [columns, open, reset]);

  const clearInvalidKey = (key) => {
    setInvalidKeys((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const onChangeImage = async ({ key, nextUrl }) => {
    setValue(key, nextUrl, {
      shouldDirty: true,
      shouldTouch: true,
    });
    clearInvalidKey(key);
    const url = String(nextUrl || "").trim();
    if (!isImageHttpUrl({ value: url })) return;
    setCheckingImageUrls((current) => {
      const next = new Set(current);
      next.add(url);
      return next;
    });
    setFailedImageUrls((current) => {
      const next = new Set(current);
      next.delete(url);
      return next;
    });
    const result = await dispatch(startRehostProduceRowImages({ urls: [url] }));
    const item = (result?.results || []).find(
      (entry) => entry?.sourceUrl === url
    );
    if (item?.ok && item.url) {
      if (item.url !== url) {
        setValue(key, item.url, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    } else {
      setFailedImageUrls((current) => {
        const next = new Set(current);
        next.add(url);
        return next;
      });
    }
    setCheckingImageUrls((current) => {
      const next = new Set(current);
      next.delete(url);
      return next;
    });
  };

  const fieldErrors = (column) => {
    if (!invalidKeys.has(column.key)) return undefined;
    return {
      [column.key]: {
        message: t(
          column.role === "highlight"
            ? TEMPLATES_PRODUCE_VALIDATION_HIGHLIGHT
            : FIELD_REQUIRED
        ),
      },
    };
  };

  const onConfirm = () => {
    const nextRow = getValues() || {};
    const byRow = getInvalidProduceCellKeysByRow({
      rows: [nextRow],
      columns,
    });
    const nextInvalid = new Set(byRow[0] || []);
    setInvalidKeys(nextInvalid);
    if (nextInvalid.size > 0) {
      SimpleAlert({
        title: t(
          hasInvalidHighlightPhrase({ rows: [nextRow], columns })
            ? TEMPLATES_PRODUCE_VALIDATION_HIGHLIGHT
            : TEMPLATES_PRODUCE_VALIDATION_ERROR
        ),
        icon: "error",
      });
      return;
    }
    if (
      rowsHaveFailedProduceImages({
        rows: [nextRow],
        columns,
        failedUrls: failedImageUrls,
      })
    ) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_IMAGES_UNUSABLE),
        icon: "error",
      });
      return;
    }
    onSubmit({ row: nextRow });
  };

  return (
    <ModalBasicLayout
      modalOpen={open}
      onCloseModal={onClose}
      confirmClose
      title={t(TEMPLATES_EDITOR_PREVIEW_VARIABLES_TITLE)}
      description={t(TEMPLATES_EDITOR_PREVIEW_VARIABLES_DESCRIPTION)}
      ariaLabel={t(TEMPLATES_EDITOR_PREVIEW_VARIABLES_TITLE)}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {columns.map((column) => {
          switch (column.type) {
            case "image": {
              const isInvalid = invalidKeys.has(column.key);
              return (
                <Box
                  key={column.key}
                  sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                >
                  <Typography sx={produceFieldLabelSx}>
                    {column.label}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "fit-content",
                      borderRadius: "10px",
                      bgcolor: isInvalid
                        ? TASK_ALERT_ERROR_MESSAGE_BG
                        : "transparent",
                      boxShadow: isInvalid
                        ? `inset 0 0 0 2px ${TASK_ALERT_ERROR_MESSAGE_BORDER}`
                        : "none",
                    }}
                  >
                    <ProduceImageCell
                      url={row?.[column.key] || ""}
                      status={getProduceImageCellStatus({
                        url: row?.[column.key] || "",
                        checkingUrls: checkingImageUrls,
                        failedUrls: failedImageUrls,
                      })}
                      selectAsButton
                      onChange={(nextUrl) =>
                        onChangeImage({ key: column.key, nextUrl })
                      }
                    />
                  </Box>
                </Box>
              );
            }
            case "string":
              return (
                <TextField
                  key={column.key}
                  control={control}
                  name={column.key}
                  label={column.label}
                  variant="filled"
                  fullWidth
                  multiline
                  rows={2}
                  rules={optionalRules}
                  placeholder={column.originExample || ""}
                  errors={fieldErrors(column)}
                  actionsChange={() => clearInvalidKey(column.key)}
                  sx={{ ...produceSelectSx, ...resizableMultilineSx }}
                />
              );
            default: {
              const exhaustive = column.type;
              throw new Error(`Unhandled preview column type: ${exhaustive}`);
            }
          }
        })}
      </Box>
      <DialogActions
        sx={{
          px: {
            xs: 3,
            sm: 5,
          },
          pb: 3,
          mt: 0.5,
        }}
      >
        <PreviewVariablesActions
          isCheckingImages={isCheckingImages}
          onConfirm={onConfirm}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default TemplateEditorPreviewVariablesModal;
